// ─────────────────────────────────────────────────────────────────────────────
// WHICH VAT RATE A RECURRING LINE CARRIES — the template screen's copy of the rule.
//
// THE TWIN OF "Aidey Backend/lib/recurringTax.js", AND DELIBERATELY A COPY.
//
// AF-003 was not that the rate was wrong. It was that this screen PREVIEWED a total including
// the template's rate while the cron produced an invoice without it — one rule, two
// implementations, and the owner trusting the one on screen. So a second implementation is
// exactly what caused the defect, and shipping one on purpose needs a reason.
//
// The reason is that the browser cannot require a file from the backend package, and the
// alternative — asking the server to price a template on every keystroke — puts a network round
// trip inside a preview. What makes the copy safe is that it is NOT trusted to agree: both files
// are pinned to the same committed table, "Aidey Backend/ci/recurring_tax_cases.json", by
// recurringTax.test.js here and by tests/recurringTax.test.js there. Editing one rule without the
// other fails a test in the other codebase.
//
// If this file and its twin ever need to disagree, that is a product decision, and the table is
// where it gets written down.
//
// THE THREE STATES:
//   tax absent / null / ''   follows the template rate — the unanswered state, and the default
//   tax: 0                   no VAT, deliberately — written only when a person chooses it
//   tax: <n>                 this line's own rate
// ─────────────────────────────────────────────────────────────────────────────

import { invoiceAmounts } from './invoiceAmounts';

export function hasOwnTax(line) {
  if (!line || typeof line !== 'object') return false;
  const v = line.tax;
  if (v === null || v === undefined || v === '') return false;
  if (typeof v === 'boolean') return false;
  return Number.isFinite(Number(v));
}

export function resolveLineTax(line, templateRate) {
  if (hasOwnTax(line)) return { rate: Number(line.tax), source: 'line' };
  const rate = Number(templateRate);
  return { rate: Number.isFinite(rate) ? rate : 0, source: 'template' };
}

export function applyTemplateTax(lineItems, templateRate) {
  const items = Array.isArray(lineItems) ? lineItems : [];
  const counts = { line: 0, template: 0 };
  const lines = items.map((li) => {
    const { rate, source } = resolveLineTax(li, templateRate);
    counts[source] += 1;
    return { ...li, tax: rate };
  });
  return { lines, counts, mixed: counts.line > 0 && counts.template > 0 };
}

// The preview's totals, computed from the SAME resolved rates the cron will store. The old
// preview did `subtotal * templateRate / 100`, which could not express a line opting out at all.
// Item 34: through the one rounding rule (./invoiceAmounts, per line), as the generated invoice is.
export function previewTotals(lineItems, templateRate, currency = 'GBP') {
  const { lines, counts, mixed } = applyTemplateTax(lineItems, templateRate);
  const { subtotal, tax, total } = invoiceAmounts({ lineItems: lines, currency });
  return { subtotal, tax, total, counts, mixed };
}
